import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, CheckCircle, XCircle, Eye } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CERT_LABELS = {
  gst: 'GST', udyam: 'Udyam / MSME', shop_act: 'Shop & Establishment',
  msme: 'MSME', fssai: 'FSSAI', trade_license: 'Trade License', other: 'Other Govt',
};
const ID_LABELS = {
  aadhaar: 'Aadhaar', pan: 'PAN', voter: 'Voter ID', passport: 'Passport', driving_license: 'Driving License',
};
const PROOF_LABELS = {
  training_cert: 'Training Certificate',
  work_id: 'Work ID Proof',
  social_media: 'Social Media Page',
};

const STATUS_BADGE = {
  pending_review: { color: 'bg-blue-100 text-blue-800 border-blue-300', label: 'Pending' },
  approved: { color: 'bg-green-100 text-green-800 border-green-300', label: 'Approved' },
  rejected: { color: 'bg-red-100 text-red-800 border-red-300', label: 'Rejected' },
  not_submitted: { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', label: 'Not Submitted' },
  not_required: { color: 'bg-gray-100 text-gray-700 border-gray-300', label: 'N/A' },
};

/**
 * Admin KYC review panel.
 *
 * Two views in one card:
 *  • Tab "Pending" — only `pending_review`, oldest first; reviewer takes
 *    Approve / Reject (with mandatory reason) action.
 *  • Tab "All"     — every KYC-tracked vendor/pro with their current status.
 *
 * Selecting a row opens a dialog with both uploaded documents and the
 * Approve / Reject controls.
 */
const AdminKycPanel = () => {
  const [tab, setTab] = useState('pending');
  const [pendingRows, setPendingRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [acting, setActing] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/kyc/pending`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/kyc/all`, { withCredentials: true }),
      ]);
      setPendingRows(p.data.items || []);
      setAllRows(a.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load KYC data');
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleApprove = async (user) => {
    if (!user) return;
    setActing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/kyc/${user.id}/approve`, {}, { withCredentials: true });
      toast.success(`Approved ${user.business_name || user.name}`);
      setSelected(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approve failed');
    }
    setActing(false);
  };

  const handleReject = async (user) => {
    if (!user) return;
    if (!rejectReason.trim()) { toast.error('Rejection reason is required'); return; }
    setActing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/kyc/${user.id}/reject`,
        { reason: rejectReason.trim() },
        { withCredentials: true });
      toast.success(`Rejected ${user.business_name || user.name}`);
      setSelected(null);
      setRejectReason('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reject failed');
    }
    setActing(false);
  };

  const rows = tab === 'pending' ? pendingRows : allRows;

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="admin-kyc-panel">
      <CardHeader className="border-b border-[#E8E1D5]">
        <CardTitle className="font-heading text-2xl flex items-center gap-2">
          <ShieldCheck size={24} className="text-brand-primary" /> KYC Verification
        </CardTitle>
        <CardDescription>
          Review uploaded vendor / professional documents and approve or reject. Rejected users can re-upload corrected docs.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-5 space-y-4">
        <div className="flex gap-2">
          <button onClick={() => setTab('pending')}
            className={`px-3 py-1.5 text-xs uppercase font-bold tracking-wider border-2 ${tab === 'pending' ? 'border-brand-primary bg-brand-primary text-sand-50' : 'border-[#E8E1D5] hover:border-brand-primary/40'}`}
            data-testid="kyc-tab-pending">
            Pending ({pendingRows.length})
          </button>
          <button onClick={() => setTab('all')}
            className={`px-3 py-1.5 text-xs uppercase font-bold tracking-wider border-2 ${tab === 'all' ? 'border-brand-primary bg-brand-primary text-sand-50' : 'border-[#E8E1D5] hover:border-brand-primary/40'}`}
            data-testid="kyc-tab-all">
            All ({allRows.length})
          </button>
        </div>

        {loading ? (
          <div className="py-8 text-center text-sm text-text-secondary">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="py-8 text-center text-sm text-text-secondary italic">
            {tab === 'pending' ? 'No KYC submissions awaiting review.' : 'No KYC-tracked users yet.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-[#E8E1D5] text-text-secondary text-xs uppercase tracking-wider">
                  <th className="text-left py-2 px-2">Name / Business</th>
                  <th className="text-left py-2 px-2">Role</th>
                  <th className="text-left py-2 px-2">Phone</th>
                  <th className="text-left py-2 px-2">Status</th>
                  <th className="text-left py-2 px-2">Submitted</th>
                  <th className="text-right py-2 px-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => {
                  const badge = STATUS_BADGE[u.kyc_status] || STATUS_BADGE.not_submitted;
                  return (
                    <tr key={u.id} className="border-b border-[#E8E1D5] hover:bg-brand-accent/5" data-testid={`kyc-row-${u.id}`}>
                      <td className="py-2 px-2">
                        <div className="font-bold">{u.business_name || u.name}</div>
                        <div className="text-[10px] text-text-secondary">{u.display_id || u.id.slice(-6)}</div>
                      </td>
                      <td className="py-2 px-2 text-xs capitalize">{u.role}{u.category ? ` · ${u.category}` : ''}</td>
                      <td className="py-2 px-2 text-xs">{u.phone}</td>
                      <td className="py-2 px-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded border ${badge.color}`}>{badge.label}</span>
                      </td>
                      <td className="py-2 px-2 text-[11px] text-text-secondary">
                        {u.kyc_submitted_at ? new Date(u.kyc_submitted_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-2 px-2 text-right">
                        <Button size="sm" variant="outline"
                          onClick={() => { setSelected(u); setRejectReason(''); }}
                          className="text-xs rounded-none border-[#E8E1D5] h-7"
                          data-testid={`kyc-review-btn-${u.id}`}>
                          <Eye size={12} className="mr-1" /> Review
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle>KYC Review — {selected?.business_name || selected?.name}</DialogTitle>
            <DialogDescription>
              {selected?.role} · {selected?.phone} · status:{' '}
              <span className="font-bold">{(STATUS_BADGE[selected?.kyc_status] || {}).label}</span>
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              {selected.kyc_documents?.certificate && (
                <div className="border border-[#E8E1D5] p-3 rounded">
                  <div className="text-xs font-bold uppercase tracking-wider text-text-secondary mb-1">Certificate</div>
                  <div className="text-sm">{CERT_LABELS[selected.kyc_documents.certificate.type] || selected.kyc_documents.certificate.type}</div>
                  <a href={`${BACKEND_URL}${selected.kyc_documents.certificate.url}`} target="_blank" rel="noreferrer" className="text-brand-primary text-xs hover:underline" data-testid="admin-kyc-cert-link">Open document →</a>
                </div>
              )}
              {selected.kyc_documents?.id && (
                <div className="border border-[#E8E1D5] p-3 rounded">
                  <div className="text-xs font-bold uppercase tracking-wider text-text-secondary mb-1">Govt ID</div>
                  <div className="text-sm">{ID_LABELS[selected.kyc_documents.id.type] || selected.kyc_documents.id.type}</div>
                  <a href={`${BACKEND_URL}${selected.kyc_documents.id.url}`} target="_blank" rel="noreferrer" className="text-brand-primary text-xs hover:underline" data-testid="admin-kyc-id-link">Open document →</a>
                </div>
              )}
              {selected.kyc_documents?.profile_pic && (
                <div className="border border-[#E8E1D5] p-3 rounded flex items-start gap-3">
                  <img src={`${BACKEND_URL}${selected.kyc_documents.profile_pic.url}`} alt="profile" className="w-20 h-20 object-cover border border-[#E8E1D5]" />
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-text-secondary mb-1">Profile Picture</div>
                    <a href={`${BACKEND_URL}${selected.kyc_documents.profile_pic.url}`} target="_blank" rel="noreferrer" className="text-brand-primary text-xs hover:underline" data-testid="admin-kyc-profile-link">Open original →</a>
                  </div>
                </div>
              )}
              {selected.kyc_documents?.pro_proof && (
                <div className="border border-[#E8E1D5] p-3 rounded">
                  <div className="text-xs font-bold uppercase tracking-wider text-text-secondary mb-1">Work Proof</div>
                  <div className="text-sm">{PROOF_LABELS[selected.kyc_documents.pro_proof.type] || selected.kyc_documents.pro_proof.type}</div>
                  {selected.kyc_documents.pro_proof.url ? (
                    <a href={`${BACKEND_URL}${selected.kyc_documents.pro_proof.url}`} target="_blank" rel="noreferrer" className="text-brand-primary text-xs hover:underline" data-testid="admin-kyc-proof-link">Open file →</a>
                  ) : selected.kyc_documents.pro_proof.external_url ? (
                    <a href={selected.kyc_documents.pro_proof.external_url} target="_blank" rel="noreferrer" className="text-brand-primary text-xs hover:underline" data-testid="admin-kyc-proof-link">{selected.kyc_documents.pro_proof.external_url}</a>
                  ) : null}
                </div>
              )}
              {selected.kyc_contact_name && (
                <div className="text-xs">Contact: <strong>{selected.kyc_contact_name}</strong>{selected.kyc_business_pan ? <> · PAN: <strong>{selected.kyc_business_pan}</strong></> : null}</div>
              )}
              {selected.kyc_rejection_reason && (
                <div className="text-xs bg-red-50 border border-red-200 p-2 rounded">
                  <strong>Previous rejection:</strong> {selected.kyc_rejection_reason}
                </div>
              )}

              {selected.kyc_status === 'pending_review' && (
                <div className="border-t border-[#E8E1D5] pt-3 space-y-3">
                  <div className="flex justify-end gap-2">
                    <Button onClick={() => handleApprove(selected)} disabled={acting}
                      className="bg-green-600 hover:bg-green-700 text-white rounded-none flex items-center gap-1"
                      data-testid="kyc-approve-btn">
                      <CheckCircle size={14} /> Approve
                    </Button>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-bold text-text-secondary">Or reject with reason</Label>
                    <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="e.g. Certificate text not readable"
                      className="rounded-none border-[#E8E1D5] h-9"
                      data-testid="kyc-reject-reason" />
                    <div className="flex justify-end">
                      <Button onClick={() => handleReject(selected)} disabled={acting || !rejectReason.trim()}
                        className="bg-red-600 hover:bg-red-700 text-white rounded-none flex items-center gap-1"
                        data-testid="kyc-reject-btn">
                        <XCircle size={14} /> Reject
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AdminKycPanel;
